import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Shield, 
  Bell, 
  User, 
  Lock, 
  Globe, 
  Database, 
  Save, 
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Info,
  ToggleLeft,
  ToggleRight,
  Key,
  Eye,
  EyeOff,
  Download,
  Upload
} from 'lucide-react';
import api from '../../utils/api';

const StaffSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [initialized, setInitialized] = useState(false);
  
  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    companyName: 'Receevit® Ltd',
    timezone: 'Europe/London',
    dateFormat: 'DD/MM/YYYY',
    currency: 'GBP',
    language: 'en',
    maintenanceMode: false
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: true,
    sessionTimeout: 30,
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true
    },
    loginAttempts: 5,
    lockoutDuration: 15,
    ipWhitelist: ['192.168.1.0/24', '10.0.0.0/8']
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    email: {
      securityAlerts: true,
      systemUpdates: true,
      clientActivity: false,
      billingNotifications: true
    },
    sms: {
      securityAlerts: true,
      systemUpdates: false,
      clientActivity: false,
      billingNotifications: false
    },
    push: {
      securityAlerts: true,
      systemUpdates: true,
      clientActivity: true,
      billingNotifications: true
    }
  });

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    autoBackup: true,
    backupFrequency: 'daily',
    backupRetention: 30,
    logRetention: 90,
    performanceMonitoring: true,
    errorReporting: true
  });

  const handleGeneralSettingChange = (key, value) => {
    setGeneralSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSecuritySettingChange = (key, value) => {
    setSecuritySettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handlePasswordPolicyChange = (key, value) => {
    setSecuritySettings(prev => ({
      ...prev,
      passwordPolicy: { ...prev.passwordPolicy, [key]: value }
    }));
    setSaved(false);
  };

  const handleNotificationSettingChange = (type, key, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [type]: { ...prev[type], [key]: value }
    }));
    setSaved(false);
  };

  const handleSystemSettingChange = (key, value) => {
    setSystemSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  // Fetch settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await api.get('/api/settings');
        const settings = response.data.settings;
        
        // Update general settings
        setGeneralSettings({
          companyName: settings.company_name || 'Receevit® Ltd',
          timezone: settings.timezone || 'Europe/London',
          dateFormat: settings.date_format || 'DD/MM/YYYY',
          currency: settings.currency || 'GBP',
          language: settings.language || 'en',
          maintenanceMode: settings.maintenance_mode === 'true'
        });
        
        // Update security settings
        setSecuritySettings({
          twoFactorAuth: settings.two_factor_auth_required === 'true',
          sessionTimeout: parseInt(settings.session_timeout_minutes) || 30,
          passwordPolicy: {
            minLength: parseInt(settings.password_min_length) || 8,
            requireUppercase: settings.password_require_uppercase === 'true',
            requireLowercase: settings.password_require_lowercase === 'true',
            requireNumbers: settings.password_require_numbers === 'true',
            requireSpecialChars: settings.password_require_special_chars === 'true'
          },
          loginAttempts: parseInt(settings.max_login_attempts) || 5,
          lockoutDuration: parseInt(settings.lockout_duration_minutes) || 15,
          ipWhitelist: ['192.168.1.0/24', '10.0.0.0/8']
        });
        
        // Update system settings
        setSystemSettings({
          autoBackup: settings.auto_backup_enabled === 'true',
          backupFrequency: settings.backup_frequency || 'daily',
          backupRetention: parseInt(settings.backup_retention_days) || 30,
          logRetention: parseInt(settings.log_retention_days) || 90,
          performanceMonitoring: settings.performance_monitoring_enabled === 'true',
          errorReporting: settings.error_reporting_enabled === 'true'
        });
        
        // Update notification settings
        setNotificationSettings({
          email: {
            securityAlerts: settings.email_notifications_enabled === 'true',
            systemUpdates: settings.email_notifications_enabled === 'true',
            clientActivity: false,
            billingNotifications: true
          },
          sms: {
            securityAlerts: settings.sms_notifications_enabled === 'true',
            systemUpdates: false,
            clientActivity: false,
            billingNotifications: false
          },
          push: {
            securityAlerts: settings.push_notifications_enabled === 'true',
            systemUpdates: settings.push_notifications_enabled === 'true',
            clientActivity: true,
            billingNotifications: true
          }
        });
        
        setInitialized(true);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        setError('Failed to load settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Prepare settings for backend
      const settingsToUpdate = {
        company_name: generalSettings.companyName,
        timezone: generalSettings.timezone,
        date_format: generalSettings.dateFormat,
        currency: generalSettings.currency,
        language: generalSettings.language,
        maintenance_mode: generalSettings.maintenanceMode.toString(),
        two_factor_auth_required: securitySettings.twoFactorAuth.toString(),
        session_timeout_minutes: securitySettings.sessionTimeout.toString(),
        password_min_length: securitySettings.passwordPolicy.minLength.toString(),
        password_require_uppercase: securitySettings.passwordPolicy.requireUppercase.toString(),
        password_require_lowercase: securitySettings.passwordPolicy.requireLowercase.toString(),
        password_require_numbers: securitySettings.passwordPolicy.requireNumbers.toString(),
        password_require_special_chars: securitySettings.passwordPolicy.requireSpecialChars.toString(),
        max_login_attempts: securitySettings.loginAttempts.toString(),
        lockout_duration_minutes: securitySettings.lockoutDuration.toString(),
        auto_backup_enabled: systemSettings.autoBackup.toString(),
        backup_frequency: systemSettings.backupFrequency,
        backup_retention_days: systemSettings.backupRetention.toString(),
        log_retention_days: systemSettings.logRetention.toString(),
        performance_monitoring_enabled: systemSettings.performanceMonitoring.toString(),
        error_reporting_enabled: systemSettings.errorReporting.toString(),
        email_notifications_enabled: notificationSettings.email.securityAlerts.toString(),
        sms_notifications_enabled: notificationSettings.sms.securityAlerts.toString(),
        push_notifications_enabled: notificationSettings.push.securityAlerts.toString()
      };
      
      // Update each setting
      for (const [key, value] of Object.entries(settingsToUpdate)) {
        await api.put('/api/settings', { setting_key: key, setting_value: value });
      }
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.post('/settings/reset');
      
      if (response.data.success) {
        // Reload settings from backend after reset
        await loadSettings();
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError('Failed to reset settings. Please try again.');
      }
    } catch (error) {
      console.error('Failed to reset settings:', error);
      setError('Failed to reset settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'system', name: 'System', icon: Database }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Configure system settings and preferences</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleReset}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Success Message */}
      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
            <span className="text-green-800">Settings saved successfully!</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {!initialized && loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-blue-800">Loading settings...</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 inline mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">General Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={generalSettings.companyName}
                    onChange={(e) => handleGeneralSettingChange('companyName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={generalSettings.timezone}
                    onChange={(e) => handleGeneralSettingChange('timezone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Europe/London">Europe/London (GMT+0)</option>
                    <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
                    <option value="America/New_York">America/New_York (GMT-5)</option>
                    <option value="Asia/Tokyo">Asia/Tokyo (GMT+9)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Format
                  </label>
                  <select
                    value={generalSettings.dateFormat}
                    onChange={(e) => handleGeneralSettingChange('dateFormat', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    value={generalSettings.currency}
                    onChange={(e) => handleGeneralSettingChange('currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Maintenance Mode</h4>
                  <p className="text-sm text-gray-600">Temporarily disable access for system maintenance</p>
                </div>
                <button
                  onClick={() => handleGeneralSettingChange('maintenanceMode', !generalSettings.maintenanceMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    generalSettings.maintenanceMode ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    generalSettings.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          )}

          {/* Security Settings Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Security Settings</h3>
              
              <div className="space-y-6">
                {/* Two-Factor Authentication */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Two-Factor Authentication</h4>
                    <p className="text-sm text-gray-600">Require 2FA for all staff accounts</p>
                  </div>
                  <button
                    onClick={() => handleSecuritySettingChange('twoFactorAuth', !securitySettings.twoFactorAuth)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      securitySettings.twoFactorAuth ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      securitySettings.twoFactorAuth ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Session Timeout */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="480"
                    value={securitySettings.sessionTimeout}
                    onChange={(e) => handleSecuritySettingChange('sessionTimeout', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Password Policy */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">Password Policy</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Length
                      </label>
                      <input
                        type="number"
                        min="6"
                        max="20"
                        value={securitySettings.passwordPolicy.minLength}
                        onChange={(e) => handlePasswordPolicyChange('minLength', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={securitySettings.passwordPolicy.requireUppercase}
                          onChange={(e) => handlePasswordPolicyChange('requireUppercase', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">Require uppercase letters</label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={securitySettings.passwordPolicy.requireLowercase}
                          onChange={(e) => handlePasswordPolicyChange('requireLowercase', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">Require lowercase letters</label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={securitySettings.passwordPolicy.requireNumbers}
                          onChange={(e) => handlePasswordPolicyChange('requireNumbers', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">Require numbers</label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={securitySettings.passwordPolicy.requireSpecialChars}
                          onChange={(e) => handlePasswordPolicyChange('requireSpecialChars', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">Require special characters</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Login Attempts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Login Attempts
                    </label>
                    <input
                      type="number"
                      min="3"
                      max="10"
                      value={securitySettings.loginAttempts}
                      onChange={(e) => handleSecuritySettingChange('loginAttempts', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lockout Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="60"
                      value={securitySettings.lockoutDuration}
                      onChange={(e) => handleSecuritySettingChange('lockoutDuration', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notification Settings Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
              
              <div className="space-y-6">
                {Object.entries(notificationSettings).map(([type, settings]) => (
                  <div key={type} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-4 capitalize">
                      {type === 'email' ? 'Email Notifications' : type === 'sms' ? 'SMS Notifications' : 'Push Notifications'}
                    </h4>
                    
                    <div className="space-y-3">
                      {Object.entries(settings).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <div>
                            <span className="text-sm text-gray-700 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                            </span>
                          </div>
                          <button
                            onClick={() => handleNotificationSettingChange(type, key, !value)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              value ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              value ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Settings Tab */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">System Settings</h3>
              
              <div className="space-y-6">
                {/* Auto Backup */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Automatic Backups</h4>
                    <p className="text-sm text-gray-600">Automatically backup system data</p>
                  </div>
                  <button
                    onClick={() => handleSystemSettingChange('autoBackup', !systemSettings.autoBackup)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      systemSettings.autoBackup ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      systemSettings.autoBackup ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {systemSettings.autoBackup && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Backup Frequency
                      </label>
                      <select
                        value={systemSettings.backupFrequency}
                        onChange={(e) => handleSystemSettingChange('backupFrequency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Backup Retention (days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={systemSettings.backupRetention}
                        onChange={(e) => handleSystemSettingChange('backupRetention', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                {/* Log Retention */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Log Retention (days)
                    </label>
                    <input
                      type="number"
                      min="30"
                      max="365"
                      value={systemSettings.logRetention}
                      onChange={(e) => handleSystemSettingChange('logRetention', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Performance Monitoring */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Performance Monitoring</h4>
                    <p className="text-sm text-gray-600">Monitor system performance and resource usage</p>
                  </div>
                  <button
                    onClick={() => handleSystemSettingChange('performanceMonitoring', !systemSettings.performanceMonitoring)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      systemSettings.performanceMonitoring ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      systemSettings.performanceMonitoring ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Error Reporting */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Error Reporting</h4>
                    <p className="text-sm text-gray-600">Automatically report system errors for debugging</p>
                  </div>
                  <button
                    onClick={() => handleSystemSettingChange('errorReporting', !systemSettings.errorReporting)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      systemSettings.errorReporting ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      systemSettings.errorReporting ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffSettings;
